import { useState, useEffect, useRef } from 'react';
import { userSettingsDB, rankSnapshotsDB } from '../lib/supabase';
import { getLeagueEntries } from '../lib/riot';

/**
 * APEX v8.18 — useRankSync
 *
 * Encapsule la synchronisation du rang depuis Riot API :
 *   - refreshRank      : refresh manuel (bouton dans le header)
 *   - levelUpEvent     : détection changement de tier ou passage 100 LP (célébration)
 *   - refreshingRank   : état de chargement
 *
 * La logique de detection level-up (précédemment dans ADCTracker via useEffect)
 * est centralisée ici pour garder App.jsx propre.
 */
export function useRankSync({ userSettings, setUserSettings, rankSnapshots, setRankSnapshots }) {
  const [refreshingRank, setRefreshingRank] = useState(false);
  const [levelUpEvent, setLevelUpEvent] = useState(null);
  const prevTierRef = useRef({ tier: null, rank: null, lp: null });

  // ============ DÉTECTION LEVEL UP ============
  // V7.5.1 : déclenche un event de célébration sur tier_up ou promo_ready
  useEffect(() => {
    const tier = userSettings?.current_tier;
    const rank = userSettings?.current_rank;
    const lp = userSettings?.current_lp;

    if (!tier) return;

    const prev = prevTierRef.current;

    // Init au 1er chargement : on enregistre sans déclencher d'event
    if (prev.tier === null) {
      prevTierRef.current = { tier, rank, lp };
      return;
    }

    const TIER_ORDER = ['IRON', 'BRONZE', 'SILVER', 'GOLD', 'PLATINUM', 'EMERALD', 'DIAMOND', 'MASTER', 'GRANDMASTER', 'CHALLENGER'];
    const APEX = ['MASTER', 'GRANDMASTER', 'CHALLENGER'];

    // Détection changement de tier (Diamond → Master par ex.)
    if (prev.tier !== tier) {
      const prevIdx = TIER_ORDER.indexOf(prev.tier);
      const newIdx = TIER_ORDER.indexOf(tier);
      // Tier UP uniquement (pas de déclenchement sur démotion)
      if (newIdx > prevIdx && prevIdx >= 0) {
        const fromLabel = APEX.includes(prev.tier)
          ? prev.tier
          : `${prev.tier}${prev.rank ? ' ' + prev.rank : ''}`;
        const toLabel = APEX.includes(tier)
          ? tier
          : `${tier}${rank ? ' ' + rank : ''}`;
        setLevelUpEvent({ type: 'tier_up', from: fromLabel, to: toLabel });
      }
    }
    // Détection passage à 100 LP (série de promo) : uniquement pour tiers non-Apex
    else if (lp >= 100 && (prev.lp ?? 0) < 100 && !APEX.includes(tier)) {
      const idx = TIER_ORDER.indexOf(tier);
      const RANKS = ['IV', 'III', 'II', 'I'];
      const rankIdx = RANKS.indexOf(rank);
      let nextTierLabel;
      if (rankIdx >= 0 && rankIdx < RANKS.length - 1) {
        nextTierLabel = `${tier} ${RANKS[rankIdx + 1]}`;
      } else {
        nextTierLabel = TIER_ORDER[idx + 1] || tier;
      }
      setLevelUpEvent({ type: 'promo_ready', tier, nextTier: nextTierLabel });
    }

    prevTierRef.current = { tier, rank, lp };
  }, [userSettings?.current_tier, userSettings?.current_rank, userSettings?.current_lp]);

  // ============ REFRESH MANUEL ============
  const refreshRank = async () => {
    if (!userSettings?.riot_puuid) {
      alert('Configure ton Riot ID dans Import d\'abord');
      return;
    }
    setRefreshingRank(true);
    try {
      const entries = await getLeagueEntries(
        userSettings.riot_puuid,
        userSettings.riot_region || 'euw1'
      );
      const soloQ = entries.find(e => e.queueType === 'RANKED_SOLO_5x5');
      if (!soloQ) {
        alert('Aucun rang SoloQ trouvé sur la saison en cours');
        return;
      }

      const updated = await userSettingsDB.upsert({
        riot_game_name: userSettings.riot_game_name,
        riot_tag_line: userSettings.riot_tag_line,
        riot_puuid: userSettings.riot_puuid,
        riot_region: userSettings.riot_region,
        riot_platform: userSettings.riot_platform,
        current_tier: soloQ.tier,
        current_rank: soloQ.rank,
        current_lp: soloQ.leaguePoints,
        current_wins: soloQ.wins,
        current_losses: soloQ.losses,
        rank_updated_at: new Date().toISOString()
      });
      setUserSettings(updated);

      const snapshot = await rankSnapshotsDB.add({
        tier: soloQ.tier,
        rank: soloQ.rank,
        lp: soloQ.leaguePoints,
        wins: soloQ.wins,
        losses: soloQ.losses
      });
      setRankSnapshots(prev => [...prev, snapshot]);
    } catch (e) {
      alert('Erreur refresh rank : ' + e.message);
    } finally {
      setRefreshingRank(false);
    }
  };

  return {
    refreshingRank,
    levelUpEvent,
    setLevelUpEvent,
    refreshRank,
  };
}
